import { useState, type FormEvent } from 'react';
import { assignablePeople } from '@core/people';
import { useCanWrite } from '@core/auth/access';
import { Modal, TabPanels } from '@core/ui/Modal';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { ObjectHistory } from '../../history/ObjectHistory';
import { DeleteConfirm, FormError, ModalFooter, fieldInput as input, fieldLabel as label, readOnlyFields, submitOnCtrlEnter } from '@core/ui/form';
import { data, useTable } from '@core/store';
import { EntitySelect } from '@core/ui/EntitySelect';
import type { Row } from '@core/store/model';
import type { VacationDraft } from './planner';

const FORM_ID = 'vacation-form'; // Save живёт в футере окна, вне формы

export function VacationModal({ draft, onClose }: { draft: VacationDraft; onClose: () => void }) {
  const canWrite = useCanWrite();
  const people = useTable('person');
  const vacations = useTable('vacation');
  const [d, setD] = useState(draft);
  const [tab, setTab] = useState<'main' | 'history'>('main');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const person = people.get(d.person_id);
  const live: Row<'vacation'> | undefined = d.id ? vacations.get(d.id) : undefined;

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!d.person_id) { setError('Choose a person'); return; }
    if (d.end_date < d.start_date) { setError('End is before start'); return; }
    setBusy(true); setError(null);
    const fields = { person_id: d.person_id, start_date: d.start_date, end_date: d.end_date, note: d.note.trim() || null };
    const r = await data.applyChanges(
      d.id
        ? [{ table: 'vacation', id: d.id, set: fields, ...(live ? { expect_updated_at: live.updated_at } : {}) }]
        : [{ table: 'vacation', insert: fields }],
      { kind: d.id ? 'edit_vacation' : 'create_vacation', person_id: d.person_id },
    );
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  async function remove() {
    if (!d.id) return;
    setBusy(true);
    const r = await data.applyChanges([{ table: 'vacation', id: d.id, delete: true }], { kind: 'delete_vacation', person_id: d.person_id });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  const footer = (
    <ModalFooter form={FORM_ID} busy={busy} onCancel={onClose} readOnly={!canWrite} left={d.id && <DeleteConfirm label="Delete" question="Delete this vacation?" busy={busy} onDelete={() => void remove()} />} />
  );
  const form = (
        <form id={FORM_ID} onSubmit={(e) => void save(e)} onKeyDown={canWrite ? submitOnCtrlEnter : undefined} className="flex flex-col gap-4">
          {!draft.person_id && (
            <div className={label}>
              <span>Who</span>
              <EntitySelect label="Who" value={d.person_id || null} placeholder="Choose a person" disabled={!canWrite}
                options={assignablePeople(people.values()).map((p) => ({ id: p.id, name: p.name, color: p.color, person: true }))}
                onChange={(id) => setD({ ...d, person_id: id ?? '' })} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <label className={label}>From
              <input type="date" className={input} value={d.start_date} required onChange={(e) => e.target.value && setD({ ...d, start_date: e.target.value, end_date: e.target.value > d.end_date ? e.target.value : d.end_date })} />
            </label>
            <label className={label}>To
              <input type="date" className={input} value={d.end_date} min={d.start_date} required onChange={(e) => e.target.value && setD({ ...d, end_date: e.target.value })} />
            </label>
          </div>
          <label className={label}>Note
            <input className={input} value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} placeholder="e.g. OOO, conference" />
          </label>
          <FormError error={error} />
        </form>
  );
  const head = d.id ? <SegmentedControl role="tabs" label="Vacation card" className="mb-4" value={tab} onChange={setTab} items={[{ key: 'main', label: 'Vacation' }, { key: 'history', label: 'History' }]} /> : undefined;
  return (
    <Modal open onClose={onClose} title={d.id ? `Vacation — ${person?.name ?? ''}` : `New vacation${person ? ' — ' + person.name : ''}`} head={head} footer={footer}>
      {d.id ? <TabPanels active={tab} panels={[['main', readOnlyFields(!canWrite, form)], ['history', <ObjectHistory table="vacation" id={d.id} active={tab === 'history'} />]]} /> : readOnlyFields(!canWrite, form)}
    </Modal>
  );
}
